/**
 * 全局progressBar（加载进度条）
 * */
import { createApp, defineComponent, h, ref } from 'vue'
import { useNamespace } from '@element-plus/hooks'
import { cAF, rAF } from '@element-plus/utils'
import { easeInOutCubic } from '@element-plus/utils/dom/scroll'
import ElProgress from '../progress.vue'
import { TIMING } from './useProgressBarTypes'
import type {
  ProgressBarCfg,
  ProgressBarReturn,
  SetTimeoutVariable,
} from './useProgressBarTypes'
import type { PropType } from 'vue'
import type { ProgressInstance, ProgressProps } from '../progress'

let _globalProgressBarRaf = -1
let _globalProgressBarTimeout: SetTimeoutVariable = -1
const _globalProgressBarInstance = ref<ProgressInstance | null>(null)
export const defaultProgressBarInitialZIndex = 99999

const defaultPercision = 8
const defaultDelay = 1000
const defaultProgressProps = {
  percentage: 0,
  strokeWidth: 4,
  showText: false,
  visible: true,
  showCompleteStatus: true,
}

const STEP_COLORS: Record<string, { bg: string; mask: string }> = {
  default: {
    bg: 'rgba(236, 245, 255, 0.5)',
    mask: 'rgba(198, 226, 255, 0.65)',
  },
  success: {
    bg: 'rgba(240, 239, 245, 0.5)',
    mask: 'rgba(209, 237, 196, 0.65)',
  },
  exception: {
    bg: 'rgba(254, 240, 240, 0.5)',
    mask: 'rgba(252, 211, 211, 0.65)',
  },
  warning: {
    bg: 'rgba(253, 246, 236, 0.5)',
    mask: 'rgba(248, 227, 197, 0.65)',
  },
}

const elProgressBar = defineComponent({
  name: 'ElProgressBar',

  props: {
    showCompleteStatus: {
      type: Boolean,
      default: true,
    },
    shadow: {
      type: Boolean,
      default: true,
    },
    visible: {
      type: Boolean,
      default: true,
    },
    steps: {
      type: Array as PropType<string[]>, // 占比数据
    },
    stepDotWidth: {
      type: Number,
      default: 4, // px
    },
    zIndex: Number,
  },

  expose: ['show', 'hide', 'update'],

  data() {
    return {
      localStatusCompleteTimeout: -1,
      stepActiveTimeout: -1,
      stepActive: false,
      localStatus: '',
      localVisible: this.visible,
      localPercentage:
        Number.parseInt(this.$attrs.percentage as string, 10) || 0,
    }
  },

  computed: {
    bgSteps() {
      if (!this.steps || !this.steps.length) return ''
      const steps: string[] = [...this.steps]
      const linears: string[] = []
      const status = (this.$attrs.status || 'default') as string
      const bg = STEP_COLORS[status]?.bg ?? STEP_COLORS['default'].bg
      const mask = STEP_COLORS[status]?.mask ?? STEP_COLORS['default'].mask
      for (const step of steps) {
        const s1 = `calc(${step} - ${this.stepDotWidth}px)`
        const s2 = step
        linears.push(
          `${bg} ${s1}`,
          `${mask} ${s1}`,
          `${mask} ${s2}`,
          `${bg} ${s2}`
        )
      }
      return `linear-gradient(to right, ${linears.join(', ')})`
    },
  },

  watch: {
    localPercentage() {
      if (this.bgSteps) {
        this.stepActiveTimeout = setTimeout(() => {
          this.stepActive = false
        }, 30) as any
      }
      if (this.showCompleteStatus) {
        const status = (this.$attrs.status || 'default') as string
        if (status === 'default' && this.localPercentage >= 100) {
          this.localStatusCompleteTimeout = setTimeout(() => {
            this.localStatus = 'success'
          }, 600) as any // transition-time
        } else {
          if (this.localStatusCompleteTimeout !== -1) {
            clearTimeout(this.localStatusCompleteTimeout)
          }
          this.localStatus = ''
        }
      }
    },
  },

  beforeUnmount() {
    if (this.stepActiveTimeout !== -1) {
      clearTimeout(this.stepActiveTimeout)
      this.stepActiveTimeout = -1
    }
  },

  methods: {
    update(percentage: number) {
      if (this.bgSteps) {
        if (percentage !== this.localPercentage) {
          if (this.stepActiveTimeout !== -1) {
            clearTimeout(this.stepActiveTimeout)
            this.stepActiveTimeout = -1
          }
          this.stepActive = true
        }
      }
      this.localPercentage = percentage
    },

    show() {
      this.localVisible = true
    },

    hide() {
      this.localVisible = false
    },
  },

  render() {
    const ns = useNamespace('global-progress-bar')
    const zIndex = this.$props.zIndex || defaultProgressBarInitialZIndex
    const showShadow = !this.$attrs.color && this.$props.shadow
    const status = (this.localStatus || this.$attrs.status || '') as string
    const progressProps = {
      ...this.$attrs,
      status,
      percentage: this.localPercentage,
    } as ProgressProps
    const progressVNodeProps = {
      ...progressProps,
      ref: _globalProgressBarInstance,
    }

    return h(
      'div',
      {
        class: [
          ns.b(),
          ns.e('wrapper'),
          showShadow && ns.m('shadow'),
          status && ns.m(status),
          this.bgSteps && !this.stepActive && ns.is('step'),
          this.bgSteps && ns.m('steps'),
          !this.localVisible && ns.is('hidden'),
          this.localPercentage >= 100 && ns.is('full'),
        ],
        style: {
          zIndex,
          background: this.bgSteps || 'transparent',
        },
      },
      h<ProgressProps>(ElProgress, progressVNodeProps)
    )
  },
})

let _globalProgressBarApp: ReturnType<typeof createApp> | null = null
let _globalProgressBarVm: any
let doneActive = false

const getConfigValue = (
  config: ProgressBarCfg | number,
  prop: keyof ProgressBarCfg,
  defaultValue?: any
): any => {
  return typeof config === 'number' ? config : config[prop] ?? defaultValue
}

const defaultCallback = (delay = defaultDelay) => {
  return () => {
    _globalProgressBarTimeout = setTimeout(() => {
      clear()
    }, Math.max(0, delay))
  }
}

const returnCallback = (config: ProgressBarCfg | number) => {
  const delay = getConfigValue(config, 'delay', defaultDelay)
  if (typeof config !== 'number' && config.callback) {
    return () => {
      _globalProgressBarTimeout = setTimeout(() => {
        config.callback && config.callback(clear)
      }, Math.max(0, delay))
    }
  }
  return defaultCallback(delay)
}

const clear = (): void => {
  if (_globalProgressBarTimeout !== -1) {
    clearTimeout(_globalProgressBarTimeout)
    _globalProgressBarTimeout = -1
  }
  if (_globalProgressBarRaf !== -1) {
    cAF(_globalProgressBarRaf)
    _globalProgressBarRaf = -1
  }
  if (_globalProgressBarApp) {
    _globalProgressBarApp.unmount()
    // document.body.removeChild(_globalProgressBarVm.$el)
    _globalProgressBarApp = null
    _globalProgressBarVm = null
    _globalProgressBarInstance.value = null
  }
}

/* const easeIn = (t: number, b: number, c: number, d: number): number => {
  // t 越小，add 概率越小，add 数值越小
  return c * (t /= d) * t * t + b
}

const easeOut = (t: number, b: number, c: number, d: number): number => {
  // t 越大，add 概率越大，add 数值越大
  return c * ((t = t / d - 1) * t * t + 1) + b
} */

const linearCubic = (t: number, b: number, c: number, d: number): number => {
  return Number.parseFloat(((t * c) / d).toFixed(defaultPercision)) + b
}

const randomCubic = (c: number, d: number): number => {
  // const f1 = easeIn(t, 0, 2, d)
  // 1s = 60f
  // 5f can change
  const fstep = (c * 200) / d // expect step distance per 5 frame
  const r = Math.random() > 0.91667 ? 1 : 0
  if (!r) return 0
  const f2 = Math.random() * 1.4 + 0.1 // [0.1, 1.5] // easeOut(t, 0, 1, d)
  const s = fstep * f2
  return Number.parseFloat((r * s).toFixed(defaultPercision))
}

const normalizePercentage = (percentage: number, integer: boolean): number => {
  return integer
    ? Number.parseInt(percentage.toFixed(0), 10)
    : Math.ceil(percentage * 10000.0) / 10000
}

export function useProgressBar(
  config: ProgressBarCfg | number
): ProgressBarReturn {
  if (_globalProgressBarApp || _globalProgressBarVm) {
    clear()
  }
  const duration = getConfigValue(config, 'duration', 0)
  const maxPercentage = getConfigValue(config, 'maxPercentage', 0)
  const timing = getConfigValue(config, 'timing', 'ease-in-out')
  const callback = returnCallback(config)
  const customTiming = getConfigValue(config, 'customTiming', null)

  const props = {
    ...defaultProgressProps,
  }

  if (typeof config !== 'number') {
    const {
      percentage = defaultProgressProps.percentage,
      color,
      strokeWidth = defaultProgressProps.strokeWidth,
      showText = defaultProgressProps.showText,
      status,
      striped,
      stripedFlow,
      textInside,
      zIndex,
      visible,
      steps,
      shadow,
      format,
      showCompleteStatus = defaultProgressProps.showCompleteStatus,
    } = config
    Object.assign(props, {
      percentage,
      color,
      strokeWidth: Math.max(2, Math.min(strokeWidth, 100)),
      showText,
      status,
      striped,
      stripedFlow,
      textInside,
      zIndex,
      visible,
      steps,
      shadow,
      format,
      showCompleteStatus,
    })
  }

  const currentPercentage = ref(
    normalizePercentage(props.percentage, !!props.showText)
  )

  _globalProgressBarApp = createApp(elProgressBar, props)
  _globalProgressBarVm = _globalProgressBarApp.mount(
    document.createElement('div')
  )
  document.body.appendChild(_globalProgressBarVm.$el)

  const update = (percentage: number): void => {
    // Do not retain decimals when show text
    // default retain 4 decimal places
    const max = 100
    const min = 0
    let nextPercentage = normalizePercentage(percentage, !!props.showText)
    nextPercentage = Math.max(Math.min(max, nextPercentage), min)
    currentPercentage.value = nextPercentage
    if (_globalProgressBarVm) {
      _globalProgressBarVm.update?.(currentPercentage.value)
    }
  }

  const start = (): void => {
    if (duration === 0) return
    if (maxPercentage && maxPercentage <= currentPercentage.value) return
    doneActive = false
    exectTimingFunction(timing)
  }

  const done = (): void => {
    if (_globalProgressBarRaf !== -1) {
      cAF(_globalProgressBarRaf)
      _globalProgressBarRaf = -1
    }
    doneActive = true
    update(100)
    callback && callback()
  }

  const destroy = (): void => {
    clear()
  }

  // 线性改变
  const exectTimingFunction = (mode = 'ease-in-out'): void => {
    const percentage = currentPercentage.value
    const distance = 100 - percentage
    let randomStep = 0
    const startTime = Date.now()

    const frameFunc = () => {
      const timestamp = Date.now()
      const time = timestamp - startTime
      let nextPercentage = 0
      switch (mode) {
        case TIMING['random']:
          randomStep += randomCubic(distance, duration)
          nextPercentage = randomStep
          break
        case TIMING['linear']:
          nextPercentage = linearCubic(time, percentage, distance, duration)
          break
        case TIMING['ease-in-out']:
          nextPercentage = Number.parseFloat(
            easeInOutCubic(
              time > duration ? duration : time,
              percentage,
              distance,
              duration
            ).toFixed(defaultPercision)
          )
          break
        case TIMING['custom']:
          nextPercentage = customTiming
            ? customTiming(
                time > duration ? duration : time,
                percentage,
                distance,
                duration
              )
            : 0
          break
        default:
          nextPercentage = 100
          break
      }

      if (doneActive) {
        doneActive = false
        return
      }
      update(nextPercentage)

      if (!maxPercentage || maxPercentage > currentPercentage.value) {
        // set max percentage, must call done manually, that will set percentage up to 100, and exect callback function
        if (time < duration && currentPercentage.value < 100) {
          _globalProgressBarRaf = rAF(frameFunc)
        } else if (typeof callback === 'function') {
          _globalProgressBarRaf = -1
          if (currentPercentage.value < 100) {
            update(100)
          }
          callback && callback()
        }
      }
    }
    _globalProgressBarRaf = rAF(frameFunc)
  }

  if (duration !== 0) {
    start()
  }

  return {
    vm: _globalProgressBarVm,
    instance: _globalProgressBarInstance,
    done,
    update,
    destroy,
  }
}
